import React, { useState } from 'react';
import { Plus, Edit2, Trash2, Eye, Search, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { sampleVideos } from '../../utils/mockData';

const AdminVideos = () => {
  const [videos, setVideos] = useState(sampleVideos);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);

  const categories = ['all', 'wedding', 'memorial', 'birthday', 'special'];

  const filteredVideos = videos.filter(video => {
    const matchesSearch = video.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || video.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this video?')) {
      setVideos(videos.filter(v => v.id !== id));
      toast.success('Video deleted successfully');
    }
  };

  const VideoModal = ({ video, onClose, onSave }) => {
    const [formData, setFormData] = useState(video || {
      id: Date.now(),
      title: '',
      category: 'wedding',
      thumbnail: '',
      videoUrl: '',
      description: ''
    });

    const handleSubmit = (e) => {
      e.preventDefault();
      onSave(formData);
      onClose();
    };

    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl border border-gray-700 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-700">
            <h2 className="text-2xl font-bold text-white">
              {video ? 'Edit Video' : 'Add New Video'}
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label className="block text-white font-medium mb-2">Title</label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/50 outline-none"
                placeholder="Emma & James Wedding Story"
              />
            </div>

            <div>
              <label className="block text-white font-medium mb-2">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/50 outline-none"
              >
                <option value="wedding">Wedding</option>
                <option value="memorial">Memorial</option>
                <option value="birthday">Birthday</option>
                <option value="special">Special Event</option>
              </select>
            </div>

            <div>
              <label className="block text-white font-medium mb-2">Thumbnail URL</label>
              <input
                type="text"
                required
                value={formData.thumbnail}
                onChange={(e) => setFormData({ ...formData, thumbnail: e.target.value })}
                className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/50 outline-none"
                placeholder="/thumbnails/wedding-thumbnail.jpg"
              />
              <p className="text-gray-400 text-sm mt-1">Upload image to /public/thumbnails/ first</p>
            </div>

            <div>
              <label className="block text-white font-medium mb-2">Video URL</label>
              <input
                type="text"
                required
                value={formData.videoUrl}
                onChange={(e) => setFormData({ ...formData, videoUrl: e.target.value })}
                className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/50 outline-none"
                placeholder="/videos/wedding-video.mp4"
              />
              <p className="text-gray-400 text-sm mt-1">Upload video to /public/videos/ first</p>
            </div>

            <div>
              <label className="block text-white font-medium mb-2">Description</label>
              <textarea
                required
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="3"
                className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/50 outline-none resize-none"
                placeholder="A beautiful wedding film..."
              />
            </div>

            <div className="flex space-x-4 pt-4">
              <button
                type="submit"
                className="flex-1 px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all"
              >
                {video ? 'Update Video' : 'Add Video'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 bg-gray-700 text-white font-semibold rounded-lg hover:bg-gray-600 transition-all"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const handleSaveVideo = (videoData) => {
    if (editingVideo) {
      setVideos(videos.map(v => v.id === videoData.id ? videoData : v));
      toast.success('Video updated successfully');
    } else {
      setVideos([...videos, videoData]);
      toast.success('Video added successfully');
    }
    setEditingVideo(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Sample Videos</h1>
          <p className="text-gray-400">Manage your portfolio of sample work</p>
        </div>
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all shadow-lg"
        >
          <Plus size={20} className="mr-2" />
          Add New Video
        </button>
      </div>

      {/* Filters */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div className="relative">
            <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search videos..."
              className="w-full pl-12 pr-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/50 outline-none"
            />
          </div>

          {/* Category Filter */}
          <div className="relative">
            <Filter size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/50 outline-none appearance-none"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {cat === 'all' ? 'All Categories' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Videos Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredVideos.map((video) => (
          <div
            key={video.id}
            className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl overflow-hidden border border-gray-700 hover:border-cyan-500 transition-all group"
          >
            <div className="relative aspect-video overflow-hidden">
              <img
                src={video.thumbnail}
                alt={video.title}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute top-3 right-3">
                <span className="px-3 py-1 bg-black/70 backdrop-blur-sm text-white text-xs font-medium rounded-full border border-white/20">
                  {video.category}
                </span>
              </div>
            </div>

            <div className="p-4">
              <h3 className="text-white font-semibold mb-2 line-clamp-1">{video.title}</h3>
              <p className="text-gray-400 text-sm mb-4 line-clamp-2">{video.description}</p>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => window.open(video.videoUrl, '_blank')}
                  className="flex-1 flex items-center justify-center px-3 py-2 bg-cyan-600 text-white text-sm font-medium rounded-lg hover:bg-cyan-700 transition-all"
                >
                  <Eye size={16} className="mr-1" />
                  View
                </button>
                <button
                  onClick={() => {
                    setEditingVideo(video);
                    setIsAddModalOpen(true);
                  }}
                  className="flex-1 flex items-center justify-center px-3 py-2 bg-gray-700 text-white text-sm font-medium rounded-lg hover:bg-gray-600 transition-all"
                >
                  <Edit2 size={16} className="mr-1" />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(video.id)}
                  className="px-3 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-all"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredVideos.length === 0 && (
        <div className="text-center py-12 bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl border border-gray-700">
          <p className="text-gray-400 text-lg">No videos found</p>
        </div>
      )}

      {/* Modals */}
      {isAddModalOpen && (
        <VideoModal
          video={editingVideo}
          onClose={() => {
            setIsAddModalOpen(false);
            setEditingVideo(null);
          }}
          onSave={handleSaveVideo}
        />
      )}
    </div>
  );
};

export default AdminVideos;
